/**
 * Utilidades compartidas para workflows de PDF
 * 
 * Propósito: Eliminar código duplicado entre workflows que necesitan:
 * - Esperar generación de PDFs via polling
 * - Subir PDFs al gestor documental
 * 
 * Workflows que usan estas utilidades:
 * - RetiroWorkflow (EPP)
 * - RegisterWorkflow (Residuos)
 * - TransferenciaWorkflow (EPP)
 * - ResiduosWarehouseStockSendWorkflow (Residuos — envío desde bodega)
 */

import { PDF_POLL_CONFIG, FILE_TYPE_CODES, FOLDER_PATHS } from '../../constants/guideConstants';

/**
 * Espera a que el PDF del DTE esté disponible usando polling
 * 
 * @param {Function} dispatch - Función dispatch de Redux
 * @param {string} dteId - ID del DTE
 * @param {Function} readDTEFaceleThunk - Thunk para leer DTE Facele
 * @param {Object} options - Opciones de polling
 * @param {number} options.intervalMs - Intervalo entre intentos (default: 3000ms)
 * @param {number} options.maxAttempts - Máximo de intentos (default: 10)
 * @returns {Promise<Blob|null>} - Blob del PDF o null si timeout
 */
export const waitForPDF = async (
	dispatch,
	dteId,
	readDTEFaceleThunk,
	options = {}
) => {
	const { intervalMs = PDF_POLL_CONFIG.INTERVAL_MS, maxAttempts = PDF_POLL_CONFIG.MAX_ATTEMPTS } = options;

	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			const response = await dispatch(
				readDTEFaceleThunk({ file_type: 'pdf', dte_id: dteId })
			).unwrap();

			const data = response?.data ?? response;

			if (data instanceof Blob) {
				const contentType =
					response?.headers?.['content-type'] || data.type || '';
				if (
					contentType.includes('application/pdf') ||
					data.size > 1000
				) {
					return data;
				}
			}
		} catch (err) {
			// Continuar polling si es 404 o no está listo
			if (err?.status === 404 || /not.*ready/i.test(err?.message)) {
				// Silenciosamente continuar
			} else {
				console.warn('[waitForPDF] Error en polling:', err);
			}
		}

		if (attempt < maxAttempts) {
			await new Promise((resolve) => setTimeout(resolve, intervalMs));
		}
	}

	console.warn('[waitForPDF] Timeout esperando PDF después de', maxAttempts, 'intentos');
	return null;
};

/**
 * Sube un PDF al gestor documental
 * 
 * @param {Function} dispatch - Función dispatch de Redux
 * @param {Blob} pdfBlob - Blob del PDF
 * @param {string} fileName - Nombre del archivo (sin extensión)
 * @param {Object} options - Opciones de subida
 * @param {string} options.establishmentId - ID del establecimiento
 * @param {string} options.folderPath - Ruta de carpeta en el gestor documental
 * @param {Function} options.getFileTypeByCodeThunk - Thunk para obtener tipo de archivo
 * @param {Function} options.uploadFoldersFileThunk - Thunk para subir archivo
 * @param {string} options.fileTypeCode - Código de tipo de archivo (default: 'GD')
 * @returns {Promise<string|null>} - ID del archivo subido o null si error
 */
export const uploadDocument = async (
	dispatch,
	pdfBlob,
	fileName,
	options = {}
) => {
	const {
		establishmentId,
		folderPath = FOLDER_PATHS.EPP_RETIRO,
		getFileTypeByCodeThunk,
		uploadFoldersFileThunk,
		fileTypeCode = FILE_TYPE_CODES.GUIA_DESPACHO,
	} = options;

	if (!getFileTypeByCodeThunk || !uploadFoldersFileThunk) {
		throw new Error('uploadDocument requiere thunks de getFileTypeByCode y uploadFoldersFile');
	}

	try {
		// 1. Obtener file_type_id
		const fileTypeResponse = await dispatch(
			getFileTypeByCodeThunk(fileTypeCode)
		).unwrap();
		const fileTypeId = fileTypeResponse?._id;

		if (!fileTypeId) {
			console.error('[uploadDocument] No se encontró el tipo de archivo:', fileTypeCode);
			return null;
		}

		// 2. Crear File object
		const file = new File([pdfBlob], `${fileName}.pdf`, {
			type: 'application/pdf',
		});

		// 3. Construir FormData
		const formData = new FormData();
		formData.append('file', file);
		formData.append('file_type_id', fileTypeId);
		if (establishmentId) {
			formData.append('establishment_id', establishmentId);
		}
		formData.append('folder_paths', folderPath);

		// 4. Subir archivo
		const response = await dispatch(uploadFoldersFileThunk(formData)).unwrap();

		const uploadedFile = Array.isArray(response)
			? response[response.length - 1]
			: response;

		return uploadedFile?._id || null;
	} catch (error) {
		console.error('[uploadDocument] Error subiendo documento:', error);
		return null;
	}
};

/**
 * Genera un nombre de archivo estandarizado para guías
 * 
 * @param {Object} dteResult - Resultado de creación de DTE
 * @param {string} prefix - Prefijo opcional (ej: 'RES' para residuos)
 * @returns {string} - Nombre de archivo formateado
 */
export const generateFileName = (dteResult, prefix = '') => {
	const rut = dteResult?.data?.dtefacele?.Encabezado?.Emisor?.RUTEmisor || '';
	const folio = dteResult?.data?.folio || '';
	const baseName = `${rut}-T52-F${folio}`.replace(/\./g, '');
	return prefix ? `${prefix}-${baseName}` : baseName;
};

/**
 * Clase helper para workflows de PDF
 * Proporciona métodos estáticos con configuración por defecto
 */
export class PDFWorkflowUtils {
	/**
	 * Espera a que el PDF esté listo
	 * @static
	 */
	static waitForPDF = waitForPDF;

	/**
	 * Sube documento al gestor documental
	 * @static
	 */
	static uploadDocument = uploadDocument;

	/**
	 * Genera nombre de archivo
	 * @static
	 */
	static generateFileName = generateFileName;

	/**
	 * Configuración por defecto de polling
	 * @static
	 */
	static DEFAULT_POLL_CONFIG = PDF_POLL_CONFIG;
}

export default PDFWorkflowUtils;
